import React, { useState, useRef } from 'react'
import {
  Box,
  Button,
  TextField,
  Typography,
  LinearProgress,
  IconButton,
  CircularProgress
} from '@mui/material'
import UploadDocumentIcon from '@mui/icons-material/CloudUpload'
import TrashIcon from '@mui/icons-material/Delete'
import { fileUpload, fileDelete } from '../../api/uploadApi'
import ValidationErrorText from '../common/ValidationErrorText'

const UploadBrochure = ({
  label,
  value = { url: '', description: '' },
  onChange,
  error = '',
  setError = () => { },
  readOnly = false,
  fieldError
}) => {
  const { url = '', description = '' } = value

  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const fileInputRef = useRef(null)

  const handleFileChange = async (e) => {
    const uploaded = e.target.files[0]
    if (!uploaded) return
    if (uploaded.type !== "application/pdf") {
      alert("Only PDF files are allowed")
      setError("Only PDF files are allowed")
      return
    }
    if (uploaded.size > 50 * 1024 * 1024) {
      alert("File size should be under 50MB")
      setError("File size should be under 50MB")
      return
    }

    setError('')
    setUploading(true)
    setProgress(0)

    try {
      const data = await fileUpload(uploaded, prog => setProgress(prog))
      if (data.url) {
        onChange({ url: data.url, description })
      } else {
        setError('File upload failed (no url returned)')
      }
    } catch {
      setError('File upload failed!')
    } finally {
      setUploading(false)
      setProgress(0)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleRemoveFile = async () => {
    setError('')
    if (url) {
      try {
        await fileDelete(url)
      } catch (err) {
        setError('Failed to delete file from server')
        return
      }
    }
    onChange({ url: '', description: '' })
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  const handleDescriptionChange = (e) => {
    onChange({ url, description: e.target.value })
  }

  return (
    <Box mt={4}>
      <Typography fontSize="14px" fontWeight="500" mb={1}>
        {label}
      </Typography>
      <Typography variant="body2" mb={2}>
        Upload a clear PDF document under 50MB with legible content.
      </Typography>
      {error && (
        <Typography color="error" fontSize={13} mb={1}>{error}</Typography>
      )}

      {uploading ? (
        <Box mt={2} mb={1} display="flex" alignItems="center" gap={2}>
          <CircularProgress size={24} />
          <Typography fontSize="14px">Uploading...</Typography>
          <Box sx={{ width: '120px', mt: 1 }}>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 6, borderRadius: 5 }} />
          </Box>
        </Box>
      ) : url ? (
        <Box
          sx={{
            border: '1px solid #ccc',
            p: 2,
            maxWidth: '300px',
            borderRadius: 2,
            mb: 2,
            mt: 2
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <UploadDocumentIcon color="action" />
            <Typography fontSize="14px" sx={{ width: '100%' }}>
              <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 14 }}>
                View Uploaded Brochure
              </a>
            </Typography>
            {!readOnly && (
              <IconButton size="small" onClick={handleRemoveFile}>
                <TrashIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      ) : null}

      {url && !uploading && (
        <>
          <Typography fontSize={13} fontWeight={500} mb={0.5}>
            Description or Details about the document
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Write description"
            value={description}
            onChange={handleDescriptionChange}
            sx={{ mb: 2 }}
            InputProps={{ readOnly }}
            disabled={readOnly}
          />
        </>
      )}

      {!readOnly && (
        <Button
          variant="outlined"
          component="label"
          startIcon={<UploadDocumentIcon />}
          sx={{ color: '#1075FF', border: '1px solid #1075FF', textTransform: 'capitalize', px: 3, mt: 1 }}
          disabled={uploading}
        >
          {url ? 'Replace Brochure' : 'Upload Brochure Here'}
          <input
            type="file"
            accept="application/pdf"
            hidden
            ref={fileInputRef}
            onChange={handleFileChange}
          />
        </Button>
      )}
      <ValidationErrorText error={fieldError}/>
    </Box>
  )
}

export default UploadBrochure
